/*
 * Simam Digital — WebXR Showcases hub: hero visual.
 * A clean, slowly rotating dotted globe with two faint orbit rings, rendered
 * into a contained canvas on a flat background. No fog, no glow — restrained
 * and on-brand. Respects prefers-reduced-motion.
 *
 * Copyright (c) 2026 Simam Digital Ltd.
 */

import {
	BufferGeometry,
	Float32BufferAttribute,
	LineBasicMaterial,
	LineLoop,
	PerspectiveCamera,
	Points,
	PointsMaterial,
	Scene,
	WebGLRenderer,
} from 'three';

const GLOBE_RADIUS = 2.1;
const DOT_COUNT = 900;

function fibonacciSphere(count, radius) {
	const positions = new Float32Array(count * 3);
	const golden = Math.PI * (3 - Math.sqrt(5));
	for (let i = 0; i < count; i++) {
		const y = 1 - (i / (count - 1)) * 2;
		const ring = Math.sqrt(1 - y * y);
		const theta = golden * i;
		positions[i * 3] = Math.cos(theta) * ring * radius;
		positions[i * 3 + 1] = y * radius;
		positions[i * 3 + 2] = Math.sin(theta) * ring * radius;
	}
	return positions;
}

function buildGlobe() {
	const geometry = new BufferGeometry();
	geometry.setAttribute(
		'position',
		new Float32BufferAttribute(fibonacciSphere(DOT_COUNT, GLOBE_RADIUS), 3),
	);
	const material = new PointsMaterial({
		color: 0xffffff,
		size: 0.022,
		transparent: true,
		opacity: 0.55,
		depthWrite: false,
	});
	return new Points(geometry, material);
}

function buildRing(radius, color, opacity) {
	const segments = 128;
	const positions = new Float32Array(segments * 3);
	for (let i = 0; i < segments; i++) {
		const a = (i / segments) * Math.PI * 2;
		positions[i * 3] = Math.cos(a) * radius;
		positions[i * 3 + 1] = 0;
		positions[i * 3 + 2] = Math.sin(a) * radius;
	}
	const geometry = new BufferGeometry();
	geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
	const material = new LineBasicMaterial({ color, transparent: true, opacity });
	return new LineLoop(geometry, material);
}

export function startHero(canvas) {
	if (!canvas) return;

	const sizeOf = () => ({
		w: canvas.clientWidth || 1,
		h: canvas.clientHeight || 1,
	});

	const scene = new Scene();

	const { w, h } = sizeOf();
	const camera = new PerspectiveCamera(45, w / h, 0.1, 100);
	camera.position.set(0, 0.4, 7.2);
	camera.lookAt(0, 0, 0);

	const renderer = new WebGLRenderer({
		canvas,
		alpha: true,
		antialias: true,
		powerPreference: 'high-performance',
	});
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
	renderer.setSize(w, h, false);

	const globe = buildGlobe();
	globe.rotation.z = 0.32;

	const ringA = buildRing(GLOBE_RADIUS * 1.32, 0xff5a1f, 0.4); // brand orange
	ringA.rotation.x = Math.PI / 2.35;
	const ringB = buildRing(GLOBE_RADIUS * 1.55, 0xffffff, 0.12);
	ringB.rotation.x = Math.PI / 1.9;
	ringB.rotation.z = 0.5;

	scene.add(globe, ringA, ringB);

	function resize() {
		const s = sizeOf();
		camera.aspect = s.w / s.h;
		camera.updateProjectionMatrix();
		renderer.setSize(s.w, s.h, false);
	}
	if (typeof ResizeObserver !== 'undefined') {
		new ResizeObserver(resize).observe(canvas);
	}
	window.addEventListener('resize', resize);

	const reduceMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)',
	).matches;

	if (reduceMotion) {
		renderer.render(scene, camera);
		return;
	}

	let t = 0;
	renderer.setAnimationLoop(() => {
		t += 0.0035;
		globe.rotation.y = t;
		ringA.rotation.z = t * 0.6;
		ringB.rotation.y = -t * 0.4;
		renderer.render(scene, camera);
	});
}
